
import { useState } from 'react';
import type { ExpenseData } from '../types';
import { CURRENCIES } from '../types';

interface UserInfoStepProps {
  data: ExpenseData;
  onUpdate: (data: Partial<ExpenseData>) => void;
  onNext: () => void;
}

export default function UserInfoStep({ data, onUpdate, onNext }: UserInfoStepProps) {
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showCustomCurrency, setShowCustomCurrency] = useState(data.currencyCode === 'OTHER');

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!data.userName.trim()) {
      newErrors.userName = '请输入报销人姓名';
    }

    if (!data.expenseType) {
      newErrors.expenseType = '请选择报销类型';
    }

    // 出差报销时验证自定义币种
    if (data.expenseType === 'business' && data.currencyCode === 'OTHER') {
      if (!data.customCurrencyName?.trim()) {
        newErrors.customCurrencyName = '请输入币种名称';
      }
      if (!data.customCurrencySymbol?.trim()) {
        newErrors.customCurrencySymbol = '请输入币种符号';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateForm()) {
      onNext();
    }
  };

  const handleExpenseTypeChange = (type: 'daily' | 'business') => {
    if (type === 'daily') {
      // 日常报销默认人民币
      onUpdate({
        expenseType: type,
        currencyCode: 'CNY',
        currencySymbol: '¥',
        customCurrencyName: undefined,
        customCurrencySymbol: undefined,
      });
      setShowCustomCurrency(false);
    } else {
      // 出差报销保持当前币种设置
      onUpdate({ expenseType: type });
    }
  };

  const handleCurrencyChange = (currencyCode: string) => {
    const currency = CURRENCIES.find(c => c.code === currencyCode);
    if (currency) {
      if (currencyCode === 'OTHER') {
        setShowCustomCurrency(true);
        onUpdate({
          currencyCode: 'OTHER',
          currencySymbol: '',
        });
      } else {
        setShowCustomCurrency(false);
        onUpdate({
          currencyCode: currency.code,
          currencySymbol: currency.symbol,
          customCurrencyName: undefined,
          customCurrencySymbol: undefined,
        });
      }
    }
  };

  const handleCustomCurrencyChange = (field: 'name' | 'symbol', value: string) => {
    if (field === 'name') {
      onUpdate({ customCurrencyName: value });
    } else {
      onUpdate({
        customCurrencySymbol: value,
        currencySymbol: value, // 同步更新 currencySymbol
      });
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-gray-800 mb-2">填写基础信息</h2>
        <p className="text-gray-600">请填写报销相关的基本信息</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* 报销人姓名 */}
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            报销人姓名 <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={data.userName}
            onChange={(e) => onUpdate({ userName: e.target.value })}
            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
              errors.userName ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="请输入您的姓名"
          />
          {errors.userName && (
            <p className="mt-1 text-sm text-red-500">{errors.userName}</p>
          )}
        </div>

        {/* 报销类型 */}
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            报销类型 <span className="text-red-500">*</span>
          </label>
          <div className="grid grid-cols-2 gap-4">
            <button
              type="button"
              onClick={() => handleExpenseTypeChange('daily')}
              className={`p-4 border-2 rounded-lg transition-all cursor-pointer ${
                data.expenseType === 'daily'
                  ? 'border-blue-500 bg-blue-50 text-blue-700'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center justify-center mb-2">
                <i className="ri-calendar-line text-2xl"></i>
              </div>
              <div className="font-medium">日常报销</div>
              <div className="text-sm text-gray-500 mt-1">日常办公费用报销</div>
            </button>

            <button
              type="button"
              onClick={() => handleExpenseTypeChange('business')}
              className={`p-4 border-2 rounded-lg transition-all cursor-pointer ${
                data.expenseType === 'business'
                  ? 'border-blue-500 bg-blue-50 text-blue-700'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center justify-center mb-2">
                <i className="ri-plane-line text-2xl"></i>
              </div>
              <div className="font-medium">出差报销</div>
              <div className="text-sm text-gray-500 mt-1">出差相关费用报销</div>
            </button>
          </div>
          {errors.expenseType && (
            <p className="mt-1 text-sm text-red-500">{errors.expenseType}</p>
          )}
        </div>

        {/* 币种选择 - 仅出差报销显示 */}
        {data.expenseType === 'business' && (
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              报销币种 <span className="text-red-500">*</span>
            </label>
            <select
              value={data.currencyCode}
              onChange={(e) => handleCurrencyChange(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all bg-white cursor-pointer"
            >
              {CURRENCIES.map(currency => (
                <option key={currency.code} value={currency.code}>
                  {currency.symbol ? `${currency.symbol} ` : ''}{currency.name}
                </option>
              ))}
            </select>

            {/* 自定义币种输入 */}
            {showCustomCurrency && (
              <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <p className="text-sm text-gray-600 mb-3">请输入自定义币种信息：</p>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      币种名称 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={data.customCurrencyName || ''}
                      onChange={(e) => handleCustomCurrencyChange('name', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        errors.customCurrencyName ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="如：韩元 (KRW)"
                    />
                    {errors.customCurrencyName && (
                      <p className="mt-1 text-sm text-red-500">{errors.customCurrencyName}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      币种符号 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={data.customCurrencySymbol || ''}
                      onChange={(e) => handleCustomCurrencyChange('symbol', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        errors.customCurrencySymbol ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="如：₩ 或 KRW"
                    />
                    {errors.customCurrencySymbol && (
                      <p className="mt-1 text-sm text-red-500">{errors.customCurrencySymbol}</p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* 项目信息 */}
        {data.expenseType && (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                项目名称 <span className="text-gray-400">(可选)</span>
              </label>
              <input
                type="text"
                value={data.projectName}
                onChange={(e) => onUpdate({ projectName: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                placeholder={data.expenseType === 'daily' ? '请输入项目名称（可选）' : '请输入项目名称（可选）'}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                项目编码 <span className="text-gray-400">(可选)</span>
              </label>
              <input
                type="text"
                value={data.projectCode}
                onChange={(e) => onUpdate({ projectCode: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                placeholder="请输入项目编码（可选）"
              />
            </div>
          </>
        )}

      </div>

      {/* 下一步按钮 */}
      <div className="flex justify-end pt-6">
        <button
          onClick={handleNext}
          className="px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium whitespace-nowrap cursor-pointer"
        >
          下一步 <i className="ri-arrow-right-line ml-2"></i>
        </button>
      </div>
    </div>
  );
}
